import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const SLIDES = [
  {
    title: (name) => `Welcome to DanceHall, ${name}!`,
    body: "Your studio is set up. Let's get you ready to start enrolling students in just a few steps.",
    icon: (
      <div className="w-16 h-16 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21" />
        </svg>
      </div>
    )
  },
  {
    title: () => '3 steps to your first student',
    body: null,
    steps: [
      { num: '1', label: 'Connect Stripe', desc: 'Accept payments from students securely' },
      { num: '2', label: 'Create your first offering', desc: 'Set up a class with schedule and pricing' },
      { num: '3', label: 'Share your enrollment link', desc: 'Students sign up and pay online' }
    ],
    icon: null
  }
];

const WelcomeModal = ({ studioName, onDismiss }) => {
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  const markSeen = async () => {
    try {
      await api.patch('/studio/onboarding', { onboardingModalSeen: true });
    } catch (e) {
      console.error('Failed to mark onboarding modal seen:', e.message);
    }
  };

  const handleConnectStripe = async () => {
    await markSeen();
    onDismiss();
    navigate('/dashboard/settings?tab=integrations');
  };

  const handleLater = async () => {
    await markSeen();
    onDismiss();
  };

  const current = SLIDES[slide];
  const isLast = slide === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Brand header bar */}
        <div className="h-2 bg-brand-500" />

        <div className="p-8">
          {current.icon}

          <h2 className="text-xl font-bold text-gray-900 text-center mb-3">
            {current.title(studioName)}
          </h2>

          {current.body && (
            <p className="text-gray-600 text-center text-sm leading-relaxed mb-6">
              {current.body}
            </p>
          )}

          {current.steps && (
            <div className="space-y-4 mb-6">
              {current.steps.map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-brand-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {step.num}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{step.label}</p>
                    <p className="text-xs text-gray-500">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col gap-2">
            {!isLast ? (
              <button
                onClick={() => setSlide(slide + 1)}
                className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-brand-600 transition-colors"
              >
                Next
              </button>
            ) : (
              <>
                <button
                  onClick={handleConnectStripe}
                  className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-brand-600 transition-colors"
                >
                  Connect Stripe Now
                </button>
                <button
                  onClick={handleLater}
                  className="w-full text-gray-500 py-2 rounded-lg text-sm font-medium hover:text-gray-700 transition-colors"
                >
                  I'll do this later
                </button>
              </>
            )}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === slide ? 'bg-brand-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
